import { useCallback } from "react"
import { DateTime } from "luxon"
import { download, generateCsv, mkConfig } from "export-to-csv"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../localization/useDateTime"
import { CardType, MerchantIdCards } from "../idCardsTypes"
import { toDinero } from "../../../../currency/currencyLib"
import { useMoney } from "../../../../currency/useMoney"

type ExportCsvParams = Readonly<{
    data: MerchantIdCards[] | undefined
    startDate: DateTime
    endDate: DateTime
}>

export function useExportIdCards({ data, startDate, endDate }: ExportCsvParams) {
    const dateTimeFactory = useDateTime()
    const moneyFactory = useMoney({ locale: "da-DK" })

    const { l10n } = useAppLocalization()

    const csvOptions = mkConfig({
        fieldSeparator: ";",
        quoteStrings: true,
        decimalSeparator: ",",
        useBom: true,
        useKeysAsHeaders: true,
        filename: `Facilitynet-id-cards-${dateTimeFactory.formatDate(startDate)}_${dateTimeFactory.formatDate(endDate)}`,
    })
    const mapOrderToCsvRow = (card: MerchantIdCards) => {
        return {
            "Card type": card?.paymentCard?.cardType ?? "",
            "Card name": card?.paymentCard?.name ?? "",
            "Card number": card?.paymentCard?.cardNumber ?? "",
            "Card status":
                card?.paymentCard?.isActive !== undefined
                    ? l10n.getString(card?.paymentCard?.isActive ? "active" : "inactive")
                    : "",
            "Employee ID": card?.user?.employeeId ?? "",
            "Employee name": card?.user?.displayName ?? "",
            "Employee email": card?.user?.email ?? "",
            "Wallet balance":
                card?.paymentCard?.cardType === CardType.TOP_UP
                    ? moneyFactory.format(toDinero(card.user.account.balance))
                    : "",
            "Company name": card?.user?.company?.name ?? "",
        }
    }

    return useCallback(() => {
        let exportRows: ReturnType<typeof mapOrderToCsvRow>[]

        if (data && data.length > 0) {
            exportRows = data.map((card) => mapOrderToCsvRow(card))
        } else {
            exportRows = [mapOrderToCsvRow({} as MerchantIdCards)]
        }

        const csv = generateCsv(csvOptions)(exportRows)
        download(csvOptions)(csv)
    }, [dateTimeFactory, moneyFactory, data, startDate, endDate, l10n])
}
